import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useEffect } from "react";
import InternalPage from "../../../components/layout/internal/InternalPage";
import { selectEntryByKey } from "../../../store/slices/formEntriesSlice";
import { RootState } from "../../../store";
import RegistrantCard from "./RegistrantCard";
import GeneralInfoSection from "./GeneralInfoSection";
import RegistrantHeader from "./RegistrantHeader";
import BazaarTableCard from "./BazaarTableCard";
import NotesSection from "./NotesSection";
import TransportationRequestCard from "./TransportationRequestCard";
import { getRegistrantsContainer } from "../../../utils/utils";
import { formMappings } from "../../../config/formMappings";
import LoadingComponent from "../../../components/LoadingComponent";

export default function RegistrantPage() {
  const { registrantKey } = useParams();
  const navigate = useNavigate();

  // Get form mapping from Redux state (must be called before any conditional returns)
  const activeFormName = useSelector(
    (state: RootState) => state.activeForm.activeFormName
  );
  const formMapping = formMappings[activeFormName as keyof typeof formMappings];
  const isVersion1 = formMapping?.version === "1";
  const hasTransportationRequest =
    formMapping && "transportationRequest" in formMapping;

  // Check if entries are currently loading
  const loadingEntries = useSelector(
    (state: RootState) => state.formEntries.loading
  );

  // Check if entries have been loaded at least once
  const allEntries = useSelector(
    (state: RootState) => state.formEntries.allEntries
  );
  const hasEntriesLoaded = allEntries.length > 0;

  const entry = useSelector((state: RootState) =>
    selectEntryByKey(state, registrantKey || "")
  );

  const registrantsContainer = entry
    ? getRegistrantsContainer(entry.meta)
    : null;

  // Scroll to top when component mounts or registrantKey changes
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    // Also scroll the main content area if it exists
    const mainElement = document.querySelector("main");
    if (mainElement) {
      mainElement.scrollTop = 0;
    }
  }, [registrantKey]);

  useEffect(() => {
    // Only redirect to 404 if:
    // 1. Entries have finished loading (not currently loading)
    // 2. Entries have been loaded at least once (hasEntriesLoaded)
    // 3. Entry is still not found
    if (
      !loadingEntries &&
      hasEntriesLoaded &&
      (!entry || !registrantsContainer)
    ) {
      navigate("/404", { replace: true });
    }
  }, [entry, registrantsContainer, loadingEntries, hasEntriesLoaded, navigate]);

  return (
    <InternalPage>
      {/* Show loading state while entries are loading or haven't loaded yet */}
      {loadingEntries || !hasEntriesLoaded ? (
        <LoadingComponent />
      ) : entry && registrantsContainer ? (
        <>
          <RegistrantHeader entry={entry} />
          <div className="grid grid-cols-1 gap-x-0 sm:gap-x-5 gap-y-5">
            {Object.entries(registrantsContainer)
              .filter(([key]) => key !== "form")
              .map(([_, registrant], registrantIdx) => (
                <RegistrantCard
                  key={registrantIdx}
                  registrant={registrant as any}
                  idx={registrantIdx}
                  entry={entry}
                />
              ))}
            <GeneralInfoSection entry={entry} />
            {isVersion1 && <BazaarTableCard entry={entry} />}
            {!isVersion1 && <NotesSection entry={entry} />}
            {hasTransportationRequest && (
              <TransportationRequestCard entry={entry} />
            )}
          </div>
        </>
      ) : (
        // Show loading while redirect to 404 is processing
        <LoadingComponent />
      )}
    </InternalPage>
  );
}
